import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useEffect } from 'react';

export default function Dashboard() {
  const { user, profile, loading, signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-carland-light flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-full border-4 border-carland-beige border-t-carland-dark animate-spin mx-auto mb-4"></div>
          <p className="text-carland-muted text-sm">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (!user || !profile) return null;

  const handleLogout = async () => {
    await signOut();
    navigate('/', { replace: true });
  };

  return (
    <div className="min-h-screen bg-carland-light">
      {/* Dashboard Header */}
      <header className="bg-white border-b border-carland-beige/50">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <a href="/" className="flex items-center gap-2">
                <div className="w-9 h-9 rounded-full bg-carland-dark flex items-center justify-center">
                  <span className="text-white font-pacifico text-base">C</span>
                </div>
                <span className="text-lg font-bold text-carland-dark">کارلند</span>
              </a>
            </div>
            <div className="flex items-center gap-4">
              <button
                onClick={handleLogout}
                className="px-4 py-2 rounded-full text-sm font-medium text-red-600 hover:bg-red-50 transition-colors duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-logout-box-r-line ml-1.5"></i>
                خروج
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Dashboard Content */}
      <main className="py-10">
        <div className="px-6 md:px-12 lg:px-20">
          <div className="max-w-4xl mx-auto">
            {/* Welcome Card */}
            <div className="bg-white rounded-2xl p-6 md:p-8 mb-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-full bg-carland-dark flex items-center justify-center flex-shrink-0">
                  <span className="text-white text-xl font-bold">
                    {profile.full_name ? profile.full_name.charAt(0) : 'C'}
                  </span>
                </div>
                <div className="text-right">
                  <h1 className="text-xl md:text-2xl font-bold text-carland-dark">
                    خوش آمدید، {profile.full_name || 'کاربر گرامی'}
                  </h1>
                  <p className="text-carland-muted text-sm mt-1">
                    {profile.role === 'employer' ? 'کارفرما' : 'کارجو'} • عضو از {new Date(profile.created_at).toLocaleDateString('fa-IR')}
                  </p>
                </div>
              </div>

              {/* Status Badge */}
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
                profile.is_approved
                  ? 'bg-green-100 text-green-700'
                  : 'bg-amber-100 text-amber-700'
              }`}>
                <span className={`w-2 h-2 rounded-full ${profile.is_approved ? 'bg-green-500' : 'bg-amber-500'}`}></span>
                {profile.is_approved ? 'حساب تایید شده' : 'در انتظار تایید'}
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white rounded-2xl p-5 text-center">
                <div className="w-10 h-10 rounded-xl bg-carland-cream flex items-center justify-center mx-auto mb-3">
                  <i className="ri-calendar-check-line text-lg text-carland-dark"></i>
                </div>
                <p className="text-2xl font-bold text-carland-dark">۱۳+</p>
                <p className="text-carland-muted text-xs mt-1">سال سابقه کارلند</p>
              </div>
              <div className="bg-white rounded-2xl p-5 text-center">
                <div className="w-10 h-10 rounded-xl bg-carland-cream flex items-center justify-center mx-auto mb-3">
                  <i className="ri-user-star-line text-lg text-carland-dark"></i>
                </div>
                <p className="text-2xl font-bold text-carland-dark">۴۷,۰۰۰+</p>
                <p className="text-carland-muted text-xs mt-1">کاربر ثبت‌نامی</p>
              </div>
              <div className="bg-white rounded-2xl p-5 text-center">
                <div className="w-10 h-10 rounded-xl bg-carland-cream flex items-center justify-center mx-auto mb-3">
                  <i className="ri-shield-check-line text-lg text-carland-dark"></i>
                </div>
                <p className="text-2xl font-bold text-carland-dark">۱۰۰٪</p>
                <p className="text-carland-muted text-xs mt-1">امنیت شغلی</p>
              </div>
            </div>

            {/* Profile Info Card */}
            <div className="bg-white rounded-2xl p-6 md:p-8">
              <h2 className="text-lg font-bold text-carland-dark mb-5 text-right">اطلاعات حساب</h2>
              <div className="space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-4 border-b border-carland-beige/50">
                  <span className="text-carland-muted text-sm">نام و نام خانوادگی</span>
                  <span className="text-carland-dark font-medium text-sm text-right">{profile.full_name || '—'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-4 border-b border-carland-beige/50">
                  <span className="text-carland-muted text-sm">ایمیل</span>
                  <span className="text-carland-dark font-medium text-sm text-right" dir="ltr">{user.email}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-4 border-b border-carland-beige/50">
                  <span className="text-carland-muted text-sm">شماره تماس</span>
                  <span className="text-carland-dark font-medium text-sm text-right" dir="ltr">{profile.phone || '—'}</span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 pb-4 border-b border-carland-beige/50">
                  <span className="text-carland-muted text-sm">نوع کاربری</span>
                  <span className="text-carland-dark font-medium text-sm text-right">
                    {profile.role === 'employer' ? 'کارفرما' : 'کارجو'}
                  </span>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                  <span className="text-carland-muted text-sm">وضعیت حساب</span>
                  <span className={`text-sm font-medium ${profile.is_approved ? 'text-green-600' : 'text-amber-600'}`}>
                    {profile.is_approved ? 'تایید شده' : 'در انتظار تایید'}
                  </span>
                </div>
              </div>

              {!profile.is_approved && (
                <div className="mt-6 p-4 rounded-xl bg-carland-cream/50 text-right">
                  <p className="text-carland-muted text-sm flex items-start gap-2">
                    <i className="ri-information-line mt-0.5"></i>
                    <span>
                      حساب شما در انتظار تایید ادمین است. برای تسریع در فرآیند تایید، از طریق تلگرام با{' '}
                      <strong className="text-carland-dark">@CarLandSupport</strong> در ارتباط باشید.
                    </span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}